import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

// iter-79: "Dark editorial glass card" login — fullscreen deep-emerald with
// ambient glows + grain, form floats in a frosted-glass card center-screen.
// No hero photo dependency, ships instantly, sets a premium tone that
// contrasts with the old AI-shield illustration.
export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome, ${user.name.split(" ")[0]}!`);
      const dest = user.role === "superadmin" ? "/superadmin" : user.role === "admin" ? "/admin" : "/dashboard";
      nav(loc.state?.from || dest, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen relative overflow-hidden text-white" style={{ background: "#042f2a" }}>
      {/* Local styles — grain + ambient glows. Keeps the file self-contained
          without touching the global Tailwind config. */}
      <style>{`
        .cco-grain {
          background-image: radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px);
          background-size: 3px 3px;
        }
        @keyframes ccoFloatIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .cco-float { animation: ccoFloatIn 0.55s cubic-bezier(.22,.61,.36,1) both; }
        @media (prefers-reduced-motion: reduce) { .cco-float { animation: none; } }
      `}</style>

      {/* Ambient glows */}
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(16,185,129,0.20)" }} aria-hidden="true" />
      <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(52,211,153,0.12)" }} aria-hidden="true" />
      <div className="absolute inset-0 cco-grain opacity-70 pointer-events-none" aria-hidden="true" />

      {/* Nav bar */}
      <div className="relative z-10 flex items-center justify-between px-6 md:px-10 py-6">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded-lg bg-emerald-500 grid place-items-center font-display font-bold text-white group-hover:bg-emerald-400 transition-colors">C</div>
          <span className="font-display text-lg font-semibold">
            ChildCheck<span className="text-emerald-300">Oh</span>
          </span>
        </Link>
        <Link to="/" className="text-sm text-emerald-200/80 hover:text-white transition-colors">
          ← Back to childcheckoh.com
        </Link>
      </div>

      {/* Centered glass card */}
      <div className="relative z-10 grid place-items-center px-6 pb-16" style={{ minHeight: "calc(100vh - 180px)" }}>
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="cco-float w-full max-w-md rounded-3xl border border-white/15 shadow-2xl p-8 md:p-9"
          style={{ background: "rgba(255,255,255,0.08)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)" }}
        >
          <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-300">Center dashboard · sign in</p>
          <h1 className="mt-1.5 font-display text-4xl font-medium">Welcome back.</h1>
          <p className="mt-1 text-emerald-100/70 text-sm">Your center is one login away.</p>

          <div className="mt-7 space-y-4">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-widest text-emerald-200/70">Email</Label>
              <Input
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                required
                autoComplete="email"
                autoFocus
                className="mt-1.5 h-12 rounded-xl bg-white/10 border-white/20 px-4 text-white placeholder:text-white/40 focus-visible:border-emerald-400 focus-visible:ring-2 focus-visible:ring-emerald-400/25 focus-visible:ring-offset-0"
                placeholder="you@yourcenter.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[10px] font-bold uppercase tracking-widest text-emerald-200/70">Password</Label>
                <a
                  href="mailto:info@childcheckoh.com?subject=Password%20reset%20-%20ChildCheckOh"
                  className="text-xs font-medium text-emerald-300 hover:text-emerald-200 hover:underline"
                  data-testid="login-forgot-link"
                >
                  Forgot?
                </a>
              </div>
              <div className="mt-1.5 relative">
                <Input
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  className="h-12 rounded-xl bg-white/10 border-white/20 px-4 pr-11 text-white placeholder:text-white/40 focus-visible:border-emerald-400 focus-visible:ring-2 focus-visible:ring-emerald-400/25 focus-visible:ring-offset-0"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="login-toggle-password"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/90 transition-colors p-1"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="mt-6 w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold flex items-center justify-center gap-2 disabled:opacity-70 transition-colors"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Signing in…</>
            ) : (
              <>Sign in <ArrowRight className="w-4 h-4" /></>
            )}
          </Button>

          <div className="mt-8 pt-6 border-t border-white/10">
            <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-200/60 mb-3">
              Trusted by Ohio-licensed centers
            </p>
            <div className="flex items-center -space-x-2">
              {[
                { label: "CE", bg: "rgba(52,211,153,0.55)" },
                { label: "LB", bg: "rgba(251,146,60,0.55)" },
                { label: "SD", bg: "rgba(168,85,247,0.55)" },
                { label: "RE", bg: "rgba(96,165,250,0.55)" },
              ].map((a) => (
                <div key={a.label} className="w-8 h-8 rounded-full border-2 grid place-items-center text-[11px] font-bold" style={{ background: a.bg, borderColor: "#042f2a" }}>
                  {a.label}
                </div>
              ))}
              <div className="w-8 h-8 rounded-full grid place-items-center text-[10px] font-bold border-2 bg-white/15" style={{ borderColor: "#042f2a" }}>+12</div>
            </div>
          </div>

          <p className="mt-6 text-[11px] leading-relaxed text-emerald-100/60">
            Accounts are provisioned by ChildCheckOh for licensed childcare centers. Need access? Email
            {" "}<a href="mailto:info@childcheckoh.com" className="text-emerald-300 font-semibold hover:text-emerald-200">info@childcheckoh.com</a>
            {" "}and our team will onboard your center.
          </p>
        </form>
      </div>

      {/* Compliance footer */}
      <div className="relative z-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pb-6 text-[10px] uppercase tracking-widest font-bold text-emerald-300/60">
        <span>SOC 2 self-attested</span>
        <span aria-hidden>·</span>
        <span>OAC 5101:2-12-08 aligned</span>
        <span aria-hidden>·</span>
        <span>US-hosted</span>
      </div>
    </div>
  );
}
